import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { Copy, Plus, ShieldAlert } from 'lucide-react'
import toast from 'react-hot-toast'
import { appointmentsSyncApi, FEED_PATH, type NewSyncDevice, type SyncDevice } from '../../api/appointmentsSync'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PageSpinner } from '../../components/ui/Spinner'
import { usePageTitle } from '../../hooks/usePageTitle'
import { cn, formatDate } from '../../lib/utils'

const feedUrl = () => `${window.location.origin}${FEED_PATH}`

async function copy(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(`${label} copied`)
  } catch {
    toast.error('Copy failed — your browser may block clipboard access on non-HTTPS pages')
  }
}

/** What the admin sends to the phone's owner. The password travels in a separate message. */
function setupSteps(username: string): string {
  return [
    'Appointments calendar on your iPhone',
    '',
    '1. Open Settings → Apps → Calendar → Calendar Accounts → Add Account → Other → Add Subscribed Calendar.',
    '   (Older iOS: Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar.)',
    `2. Server: ${feedUrl()}`,
    '3. Tap Next, then enter:',
    `   User Name: ${username}`,
    '   Password: the password I am sending you separately',
    '   Keep "Use SSL" on.',
    '4. Tap Save. An "Appointments" calendar appears in the Calendar app. It is read-only and updates by itself.',
    '',
    "Please keep a passcode on your iPhone, and don't add the Calendar widget to your Lock Screen — it would show patients' names without unlocking.",
  ].join('\n')
}

/** "iPhone · iOS 18.6" when the calendar's user agent says so. */
function deviceLabel(userAgent: string | null): string | null {
  if (!userAgent) return null
  const ios = /iOS\/([\d.]+)/.exec(userAgent) ?? /iPhone OS ([\d_]+)/.exec(userAgent)
  if (ios) return `iPhone · iOS ${ios[1].replace(/_/g, '.')}`
  if (/Mac OS X|macOS/.test(userAgent)) return 'Mac'
  return 'Other device'
}

export function AppointmentsSyncSettings() {
  usePageTitle('Appointments Sync')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [name, setName] = useState('')
  const [created, setCreated] = useState<NewSyncDevice | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<SyncDevice | null>(null)

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['appointments-sync', companyId],
    queryFn: () => appointmentsSyncApi.list(companyId!),
    enabled: !!companyId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['appointments-sync', companyId] })

  const createMutation = useMutation({
    mutationFn: () => appointmentsSyncApi.create(companyId!, name.trim()),
    onSuccess: result => { setCreated(result); invalidate() },
    onError: () => toast.error('Failed to add access'),
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => appointmentsSyncApi.revoke(companyId!, id),
    onSuccess: () => { toast.success('Access revoked'); invalidate() },
    onError: () => toast.error('Failed to revoke access'),
  })

  // Closing drops the password from memory; it cannot be shown again.
  const closeAdd = () => { setAddOpen(false); setName(''); setCreated(null) }

  if (isLoading) return <PageSpinner />

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Appointments Sync</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Read-only access to the appointment calendar from iPhone Calendar. One entry per phone.
          </p>
        </div>
        <button onClick={() => setAddOpen(true)} className="btn btn-primary btn-sm">
          <Plus className="w-3.5 h-3.5" />
          Add access
        </button>
      </div>

      {devices.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-16">No phone has access yet</p>
      )}

      {devices.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Added</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Last synced</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {devices.map(d => {
                const label = deviceLabel(d.lastSyncUserAgent)
                return (
                  <tr key={d.id} className={cn('hover:bg-gray-50', d.revokedAt && 'opacity-60')}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{d.name}</p>
                      <p className="text-xs text-gray-400 font-mono">{d.username}</p>
                    </td>
                    <td className="px-4 py-3">
                      {d.revokedAt ? (
                        <span className="text-xs text-gray-500">
                          Revoked {formatDate(d.revokedAt)}{d.revokedByName ? ` by ${d.revokedByName}` : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatDate(d.createdAt)}{d.createdByName ? ` · ${d.createdByName}` : ''}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500" title={d.lastSyncUserAgent ?? undefined}>
                      {d.lastSyncAt ? formatDistanceToNow(new Date(d.lastSyncAt), { addSuffix: true }) : 'Never'}
                      {label && <span className="block text-gray-400">{label}</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!d.revokedAt && (
                        <button
                          onClick={() => setRevokeTarget(d)}
                          className="btn btn-sm bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={addOpen} onClose={closeAdd} title={created ? 'Access created' : 'Add access'} size="lg">
        {!created ? (
          <form
            onSubmit={e => { e.preventDefault(); if (name.trim()) createMutation.mutate() }}
            className="space-y-4"
          >
            <div>
              <label className="label" htmlFor="sync-device-name">Name</label>
              <input
                id="sync-device-name"
                className="input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Dr. Anna – iPhone"
                maxLength={100}
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">One entry per phone. Name it after the person and the device.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeAdd} className="btn btn-secondary btn-sm">Cancel</button>
              <button type="submit" disabled={!name.trim() || createMutation.isPending} className="btn btn-primary btn-sm">
                Create
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>Copy the password now — it is shown only once. Send it in a different message from the setup steps.</p>
            </div>
            {[
              ['Server', feedUrl()],
              ['User name', created.username],
              ['Password', created.password],
            ].map(([label, value]) => (
              <div key={label}>
                <p className="label">{label}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm break-all">{value}</code>
                  <button type="button" onClick={() => copy(value, label)} className="btn btn-secondary btn-sm">
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </button>
                </div>
              </div>
            ))}
            <div className="flex justify-between gap-2 pt-1">
              <button type="button" onClick={() => copy(setupSteps(created.username), 'Setup steps')} className="btn btn-secondary btn-sm">
                <Copy className="w-3.5 h-3.5" />
                Copy setup steps
              </button>
              <button type="button" onClick={closeAdd} className="btn btn-primary btn-sm">Done</button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => { if (revokeTarget) revokeMutation.mutate(revokeTarget.id) }}
        title="Revoke access?"
        message={`"${revokeTarget?.name ?? ''}" stops receiving appointments, and the phone's appointment calendar is emptied at its next refresh. This can't be undone.`}
        confirmLabel="Revoke"
        isPending={revokeMutation.isPending}
      />
    </div>
  )
}
